import React, { useState, useEffect, useRef } from 'react';
import { 
  Container, 
  Box, 
  TextField, 
  Button, 
  Typography, 
  Paper,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  ListItemButton,
  LinearProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  IconButton,
  Tooltip,
  Snackbar
} from '@mui/material';
import { ContentCopy } from '@mui/icons-material';
import axios from 'axios';
import { io, Socket } from 'socket.io-client';
import { SERVER_URL, API_BASE_URL } from './config';
import { mergeOpening } from './openingState';

interface AnimeOpening {
  id?: number;
  title?: string;
  videoId: string;
  startTime: number;
  endTime: number;
  difficulty?: string;
}

interface SearchResult {
  id: number;
  title: string;
}

interface Player {
  id: string;
  name: string;
  score: number;
  hasGuessed: boolean;
  guess?: string;
  isCorrect?: boolean;
  guessTime?: number;
  isReady: boolean;
}

interface MultiplayerAppProps {
  onBackToMenu: () => void;
}

function MultiplayerApp({ onBackToMenu }: MultiplayerAppProps) {
  // Connection and room state
  const [socket, setSocket] = useState<Socket | null>(null);
  const [gameMode, setGameMode] = useState<'menu' | 'create' | 'join' | 'room' | 'game' | 'finished'>('menu');
  const [roomId, setRoomId] = useState('');
  const [playerName, setPlayerName] = useState('');
  const [isHost, setIsHost] = useState(false);
  const [players, setPlayers] = useState<Player[]>([]);
  const [error, setError] = useState('');
  const [selectedDifficulties, setSelectedDifficulties] = useState<string[]>(['easy', 'medium']);
  const [roomDifficulties, setRoomDifficulties] = useState<string[]>(['easy', 'medium']);
  const [copySnackbarOpen, setCopySnackbarOpen] = useState(false);
  
  // Game state
  const [gameState, setGameState] = useState<'waiting' | 'playing' | 'revealing' | 'finished'>('waiting');
  const [currentOpening, setCurrentOpening] = useState<AnimeOpening | null>(null);
  const [currentOpeningIndex, setCurrentOpeningIndex] = useState(0);
  const [totalOpenings, setTotalOpenings] = useState(10);
  const [timeLeft, setTimeLeft] = useState(15);
  const [allPlayersReady, setAllPlayersReady] = useState(false);
  
  // Game interaction
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [hasGuessed, setHasGuessed] = useState(false);
  const [playerGuess, setPlayerGuess] = useState('');
  const [isCorrect, setIsCorrect] = useState(false);
  const [isTimeUp, setIsTimeUp] = useState(false);
  const [loading, setLoading] = useState(false);
  const [finalScores, setFinalScores] = useState<Player[]>([]);
  const [pointBreakdown, setPointBreakdown] = useState<{base: number, time: number, first?: number, difficulty: string} | null>(null);
  const [allPlayerGuesses, setAllPlayerGuesses] = useState<Array<{playerId: string, playerName: string, guess: string, isCorrect: boolean, guessTime: number}>>([]);
  
  // Keyboard navigation state
  const [selectedIndex, setSelectedIndex] = useState(0);
  const searchInputRef = useRef<HTMLInputElement>(null);
  
  // YouTube player
  const playerRef = useRef<any>(null);
  const isApiReady = useRef(false); // Tracks if API script has loaded and callback fired
  const [youTubeApiReadyState, setYouTubeApiReadyState] = useState(false); // State to trigger effects
  const [isPlaying, setIsPlaying] = useState(false);
  const [playerReady, setPlayerReady] = useState(false);
  const loadingTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const [videoErrorMessage, setVideoErrorMessage] = useState<string>('');

  // Initialize YouTube API
  useEffect(() => {
    if (window.YT && window.YT.Player) {
      if (process.env.NODE_ENV === 'development') {
        console.log("MultiplayerApp: YouTube API already loaded.");
      }
      isApiReady.current = true;
      setYouTubeApiReadyState(true);
      return;
    }

    if (!document.querySelector('script[src="https://www.youtube.com/iframe_api"]')) {
      if (process.env.NODE_ENV === 'development') {
        console.log("MultiplayerApp: Loading YouTube API script.");
      }
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      const firstScriptTag = document.getElementsByTagName('script')[0];
      if (firstScriptTag && firstScriptTag.parentNode) {
        firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
      } else {
        // Fallback if no script tags found, append to head
        document.head.appendChild(tag);
      }
    } else {
      // Script tag exists, but API might not be ready yet, or onYouTubeIframeAPIReady was missed
      // If isApiReady.current is false, it means our callback hasn't fired.
      // if (process.env.NODE_ENV === 'development') {
      //   console.log("MultiplayerApp: YouTube API script tag found, waiting for onYouTubeIframeAPIReady.");
      // }
    }

    window.onYouTubeIframeAPIReady = () => {
      if (process.env.NODE_ENV === 'development') {
        console.log("MultiplayerApp: YouTube IFrame API is ready (onYouTubeIframeAPIReady fired).");
      }
      isApiReady.current = true;
      setYouTubeApiReadyState(true);
    };

    return () => {
      if (playerRef.current) {
        playerRef.current.destroy();
      }
    };
  }, []);

  // Initialize socket connection
  useEffect(() => {
    const newSocket = io(SERVER_URL); // Connect to the server
    setSocket(newSocket);

    // Socket event listeners
    newSocket.on('room-created', (data) => {
      setRoomId(data.roomId);
      setIsHost(data.isHost);
      setGameMode('room');
      setError('');
    });

    newSocket.on('room-joined', (data) => {
      setRoomId(data.roomId);
      setIsHost(data.isHost);
      setGameMode('room');
      setError('');
    });

    newSocket.on('room-updated', (data) => {
      setPlayers(data.players);
      setGameState(data.gameState);
      setTotalOpenings(data.totalOpenings);
      if (data.difficulties) setRoomDifficulties(data.difficulties);
      if (data.currentOpening) {
        setCurrentOpening(previous => mergeOpening(previous, data.currentOpening));
      }
      if (data.currentOpeningIndex) setCurrentOpeningIndex(data.currentOpeningIndex);
      if (data.timeLeft !== undefined) setTimeLeft(data.timeLeft);
      if (data.allPlayersReady !== undefined) setAllPlayersReady(data.allPlayersReady);
    });

    newSocket.on('new-opening', (data) => {
      setCurrentOpening(data.opening);
      setCurrentOpeningIndex(data.currentOpeningIndex);
      setTotalOpenings(data.totalOpenings);
      setHasGuessed(false);
      setPlayerGuess('');
      setIsCorrect(false);
      setIsTimeUp(false);
      setSearchQuery('');
      setSearchResults([]);
      setSelectedIndex(0); // Reset keyboard navigation
      setPlayerReady(false);
      setVideoErrorMessage(''); // Clear previous video error
      setPointBreakdown(null); // Clear previous point breakdown
      setAllPlayerGuesses([]); // Clear previous round guesses
      setGameMode('game');
      
      // Auto-focus search input on desktop (not mobile)
      const isMobile = window.innerWidth <= 768;
      if (!isMobile) {
        setTimeout(() => {
          searchInputRef.current?.focus();
        }, 100);
      }
      
      // Player creation will now be handled by a useEffect hook
    });

    newSocket.on('timer-update', (data) => {
      setTimeLeft(data.timeLeft);
    });

    newSocket.on('opening-ended', (data) => {
      setGameState('revealing');
      setIsTimeUp(true);
      if (typeof data.correctAnswer === 'string') {
        setCurrentOpening(previous => previous
          ? { ...previous, title: data.correctAnswer }
          : previous
        );
      }
      
      // Store all player guesses for display
      if (data.allPlayerGuesses) {
        setAllPlayerGuesses(data.allPlayerGuesses);
      }
      
      // Find the current player's point breakdown
      if (data.players && newSocket.id) {
        const currentPlayerData = data.players.find((p: any) => p.id === newSocket.id);
        if (currentPlayerData) {
          setIsCorrect(Boolean(currentPlayerData.isCorrect));
        }
        if (currentPlayerData && currentPlayerData.pointBreakdown) {
          setPointBreakdown(currentPlayerData.pointBreakdown);
        }
      }
    });

    newSocket.on('game-finished', (data) => {
      setFinalScores(data.finalScores);
      setGameMode('finished');
    });

    newSocket.on('new-host', (data) => {
      setIsHost(data.newHostId === newSocket.id);
    });

    newSocket.on('error', (data) => {
      setError(data.message);
    });

    return () => {
      newSocket.close();
    };
  }, []);

  // Effect to create YouTube player when conditions are met
  useEffect(() => {
    const videoId = currentOpening?.videoId; // Extract videoId for dependency
    if (process.env.NODE_ENV === 'development') {
      console.log(`MultiplayerApp: Player creation effect. Conditions: gameMode=${gameMode}, videoId=${videoId}, youTubeApiReadyState=${youTubeApiReadyState}, socketConnected=${socket?.connected}`);
    }

    if (gameMode === 'game' && currentOpening && videoId && youTubeApiReadyState && socket?.connected) {
      if (process.env.NODE_ENV === 'development') {
        console.log('MultiplayerApp: Conditions met for player creation, checking for playerDiv.');
      }
      const playerDiv = document.getElementById('youtube-player');
      if (playerDiv) {
        if (process.env.NODE_ENV === 'development') {
          console.log('MultiplayerApp: playerDiv found, calling createYouTubePlayer for videoId:', videoId);
        }
        createYouTubePlayer(currentOpening); // Pass the currentOpening object from state
      } else {
        if (process.env.NODE_ENV === 'development') {
          console.error("MultiplayerApp: YouTube player div ('youtube-player') not found in DOM during player creation effect.");
        }
        setVideoErrorMessage("Error: Player element not found. Cannot load video.");
      }
    } else if (gameMode === 'game' && currentOpening && !youTubeApiReadyState) {
      if (process.env.NODE_ENV === 'development') {
        console.log("MultiplayerApp: Player creation effect: Waiting for YouTube API to be ready.");
      }
    }

    // Cleanup function to destroy player if dependencies change causing effect to re-run OR component unmounts
    return () => {
      if (playerRef.current && typeof playerRef.current.destroy === 'function') {
        // Only destroy if the effect is re-running for a different video or gameMode,
        // or if the component is unmounting.
        // createYouTubePlayer itself handles destroying the previous player if the same effect instance calls it.
        // This cleanup is more for when the conditions for player existence are no longer met.
        // For example, if gameMode changes from 'game'.
        // Or if currentOpening.videoId changes, this cleanup runs for the *old* videoId effect instance.
        if (process.env.NODE_ENV === 'development') {
          console.log("MultiplayerApp: Cleaning up YouTube player from effect (due to dependency change or unmount).");
        }
        playerRef.current.destroy();
        playerRef.current = null; // Important to nullify the ref after destroying
      }
    };
  }, [gameMode, currentOpening?.videoId, socket, youTubeApiReadyState]); // Key change: currentOpening?.videoId

  // Create YouTube player
  const createYouTubePlayer = (opening: AnimeOpening) => {
    if (process.env.NODE_ENV === 'development') {
      console.log('MultiplayerApp: createYouTubePlayer called for videoId:', opening.videoId);
    }
    // The youTubeApiReadyState and opening checks are primary in the calling useEffect.
    // isApiReady.current is a ref, good for synchronous checks if needed, but state drives effects.
    if (!youTubeApiReadyState || !opening) { // Check state here as it's more robust for effect-driven logic
      if (process.env.NODE_ENV === 'development') {
        console.warn("MultiplayerApp: createYouTubePlayer called but youTubeApiReadyState is false or no opening.");
      }
      return;
    }

    if (playerRef.current) {
      // Destroy previous player instance if it exists
      if (process.env.NODE_ENV === 'development') {
        console.log("MultiplayerApp: Destroying previous player instance in createYouTubePlayer.");
      }
      playerRef.current.destroy();
      playerRef.current = null; // Ensure ref is cleared
    }

    // setLoading(true); // REMOVED: This was causing the player div to unmount.
    setIsPlaying(false);
    setPlayerReady(false); // Explicitly set playerReady to false at the start of creation
    setVideoErrorMessage(''); // Clear previous error at the start of creation

    // Clear any existing loading timeout
    if (loadingTimeoutRef.current) {
      clearTimeout(loadingTimeoutRef.current);
    }

    // Set a 7-second timeout for loading (slightly longer for multiplayer)
    loadingTimeoutRef.current = setTimeout(() => {
      if (process.env.NODE_ENV === 'development') {
        console.warn(`Video loading timeout for: ${opening?.videoId}. Client will proceed as 'ready'.`);
      }
      setLoading(false);
      setPlayerReady(true); // Mark as ready to not block others
      setVideoErrorMessage('Video took too long to load. Skipping for this round.');
      if (socket) {
        socket.emit('player-ready', { roomId });
      }
    }, 7000);

    try {
      playerRef.current = new window.YT.Player('youtube-player', {
        height: '315',
        width: '560',
        videoId: opening.videoId,
        playerVars: {
          autoplay: 1, // Start playing immediately like singleplayer
          controls: 1,
          disablekb: 0,
          fs: 1,
          modestbranding: 1,
          rel: 0,
          start: opening.startTime,
          // Don't set end time for multiplayer - let video continue playing
          playsinline: 1,
          showinfo: 0,
          iv_load_policy: 3,
          cc_load_policy: 0,
          origin: window.location.origin,
          mute: 0,
          enablejsapi: 1
        },
        events: {
          onReady: (event: any) => {
            if (loadingTimeoutRef.current) clearTimeout(loadingTimeoutRef.current);
            // setLoading(false); // REMOVED: Global loading state not managed here anymore for success.
            setPlayerReady(true); // Player is now ready.
            event.target.setVolume(100);
            if (process.env.NODE_ENV === 'development') {
              console.log('MultiplayerApp: YouTube player onReady for videoId:', opening.videoId);
            }
            event.target.playVideo(); // Re-adding explicit playVideo() call
            if (process.env.NODE_ENV === 'development') {
              console.log('MultiplayerApp: Called playVideo() for videoId:', opening.videoId);
            }
            setVideoErrorMessage(''); // Clear error if ready
            if (socket) {
              socket.emit('player-ready', { roomId });
            }
          },
          onStateChange: (event: any) => {
            const videoIdForLog = playerRef.current?.getVideoData ? playerRef.current.getVideoData().video_id : opening.videoId;
            // if (process.env.NODE_ENV === 'development') {
            //   console.log(`MultiplayerApp: Player state changed to ${event.data} for videoId: ${videoIdForLog}`);
            // }
            if (event.data === window.YT.PlayerState.PLAYING) {
              if (loadingTimeoutRef.current) clearTimeout(loadingTimeoutRef.current);
              if (process.env.NODE_ENV === 'development') {
                console.log('MultiplayerApp: Player state changed to PLAYING for videoId:', videoIdForLog);
              }
              setIsPlaying(true);
              setVideoErrorMessage(''); // Clear error if it starts playing
            } else if (event.data === window.YT.PlayerState.ENDED) {
              if (process.env.NODE_ENV === 'development') {
                console.log('MultiplayerApp: Player state changed to ENDED for videoId:', videoIdForLog);
              }
              setIsPlaying(false);
            }
          },
          onError: (event: any) => {
            if (loadingTimeoutRef.current) clearTimeout(loadingTimeoutRef.current);
            if (process.env.NODE_ENV === 'development') {
              console.error('MultiplayerApp: YouTube Player Error:', event.data, 'for video:', opening?.videoId);
            }
            setLoading(false);
            setPlayerReady(true); // Mark as ready even on error to not block other players
            setVideoErrorMessage(`Video unavailable (error ${event.data}). Skipping for this round.`);
            if (socket) {
              socket.emit('player-ready', { roomId });
            }
          }
        }
      });
    } catch (error) {
      if (loadingTimeoutRef.current) clearTimeout(loadingTimeoutRef.current);
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to create YouTube player:', error);
      }
      setLoading(false);
      setPlayerReady(true); // Mark as ready even on error to not block other players
      setVideoErrorMessage('Failed to create video player. Skipping for this round.');
      if (socket) {
        socket.emit('player-ready', { roomId });
      }
    }
  };

  // Video starts playing automatically when created (like singleplayer)
  // No need to wait for all players to be ready to start the video

  // Handle search
  const handleSearch = async (query: string) => {
    setSearchQuery(query);
    setSelectedIndex(0); // Reset selection when search changes
    if (query.length > 0) {
      try {
        const response = await axios.get(`${API_BASE_URL}/search?q=${query}`);
        setSearchResults(response.data);
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Error searching:', error);
        }
      }
    } else {
      setSearchResults([]);
    }
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (isTimeUp || hasGuessed || searchResults.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => Math.min(prev + 1, searchResults.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => Math.max(prev - 1, 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (searchResults[selectedIndex]) {
          handleGuess(searchResults[selectedIndex].title);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setSearchResults([]);
        setSearchQuery('');
        break;
    }
  };

  // Handle guess
  const handleGuess = (title: string) => {
    if (!socket || hasGuessed) return;
    
    setHasGuessed(true);
    setPlayerGuess(title); // Store the player's guess
    const guessTime = 15 - timeLeft;
    
    socket.emit('player-guess', {
      roomId,
      guess: title,
      guessTime
    });
    
    setSearchQuery('');
    setSearchResults([]);
    setSelectedIndex(0); // Reset keyboard navigation
  };

  // Copy room ID to clipboard
  const handleCopyRoomId = async () => {
    try {
      await navigator.clipboard.writeText(roomId);
      setCopySnackbarOpen(true);
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to copy room ID:', err);
      }
    }
  };

  // Create room
  const handleCreateRoom = () => {
    if (!socket || !playerName.trim()) return;
    
    socket.emit('create-room', {
      playerName: playerName.trim(),
      totalOpenings,
      difficulties: selectedDifficulties
    });
  };

  // Join room
  const handleJoinRoom = () => {
    if (!socket || !playerName.trim() || !roomId.trim()) return;
    
    socket.emit('join-room', {
      roomId: roomId.trim().toUpperCase(),
      playerName: playerName.trim()
    });
  };

  // Start game (host only)
  const handleStartGame = () => {
    if (!socket || !isHost) return;
    
    socket.emit('start-game', { roomId });
  };

  // Next opening (host only)
  const handleNextOpening = () => {
    if (!socket || !isHost) return;
    
    socket.emit('next-opening', { roomId });
  };

  // Return to room
  const handleReturnToRoom = () => {
    setGameMode('room');
    setGameState('waiting');
    setCurrentOpening(null);
    setHasGuessed(false);
    setPlayerGuess('');
    setIsCorrect(false);
    setSearchQuery('');
    setSearchResults([]);
    setPointBreakdown(null);
    if (playerRef.current) {
      playerRef.current.destroy();
    }
  };

  // Skip opening (host only)
  const handleSkipOpening = () => {
    if (!socket || !isHost) return;
    
    if (process.env.NODE_ENV === 'development') {
      console.log('Emitting skip-opening event');
    }
    socket.emit('skip-opening', { roomId });
  };

  // Leave room
  const handleLeaveRoom = () => {
    if (socket) {
      socket.disconnect();
    }
    setGameMode('menu');
    setGameState('waiting');
    setCurrentOpening(null);
    setPlayers([]);
    setRoomId('');
    if (playerRef.current) {
      playerRef.current.destroy();
      playerRef.current = null;
    }
  };

  // Render different screens based on game mode
  if (gameMode === 'menu') {
    return (
      <>
        <Container maxWidth="md">
          {/* Title header */}
          <div className="title-header">
            <Typography variant="h3" component="h1">
              Anime Opening Quiz
            </Typography>
          </div>

          <Box className="quiz-container">
          <Paper className="quiz-paper">
            <Box className="landing-page">
              <Typography variant="h4" gutterBottom color="primary">
                Play with Friends!
              </Typography>
              
              <Box className="multiplayer-options">
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => setGameMode('create')}
                  className="multiplayer-button"
                >
                  Create Room
                </Button>
                
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => setGameMode('join')}
                  className="multiplayer-button"
                >
                  Join Room
                </Button>
                
                <Button
                  variant="text"
                  onClick={onBackToMenu}
                  className="back-button"
                >
                  ← Back to Main Menu
                </Button>
              </Box>
            </Box>
          </Paper>
          </Box>
        </Container>
        
      </>
    );
  }

  if (gameMode === 'create') {
    return (
      <>
        <Container maxWidth="md">
          {/* Title header */}
          <div className="title-header">
            <Typography variant="h3" component="h1">
              Anime Opening Quiz
            </Typography>
          </div>

          <Box className="quiz-container">
          <Paper className="quiz-paper">
            <Box className="create-room-form">
              <Typography variant="h4" gutterBottom color="primary">
                Create Room
              </Typography>
              
              <TextField
                fullWidth
                label="Your Name"
                value={playerName}
                onChange={(e) => setPlayerName(e.target.value)}
                margin="normal"
              />
              
              <Typography variant="body1" color="text.secondary" style={{ marginTop: '16px', marginBottom: '16px' }}>
                Game settings (difficulty and number of openings) can be configured in the room lobby after creation.
              </Typography>
              
              {error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {error}
                </Alert>
              )}
              
              <Box className="form-buttons">
                <Button
                  variant="contained"
                  onClick={handleCreateRoom}
                  disabled={!playerName.trim()}
                >
                  Create Room
                </Button>
                
                <Button
                  variant="text"
                  onClick={() => setGameMode('menu')}
                >
                  Back
                </Button>
              </Box>
            </Box>
          </Paper>
          </Box>
        </Container>
        
      </>
    );
  }

  if (gameMode === 'join') {
    return (
      <>
        <Container maxWidth="md">
          {/* Title header */}
          <div className="title-header">
            <Typography variant="h3" component="h1">
              Anime Opening Quiz
            </Typography>
          </div>

          <Box className="quiz-container">
          <Paper className="quiz-paper">
            <Box className="join-room-form">
              <Typography variant="h4" gutterBottom color="primary">
                Join Room
              </Typography>
              
              <TextField
                fullWidth
                label="Your Name"
                value={playerName}
                onChange={(e) => setPlayerName(e.target.value)}
                margin="normal"
              />
              
              <TextField
                fullWidth
                label="Room ID"
                value={roomId}
                onChange={(e) => setRoomId(e.target.value.toUpperCase())}
                margin="normal"
                placeholder="Enter 6-character room ID"
              />
              
              {error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {error}
                </Alert>
              )}
              
              <Box className="form-buttons">
                <Button
                  variant="contained"
                  onClick={handleJoinRoom}
                  disabled={!playerName.trim() || !roomId.trim()}
                >
                  Join Room
                </Button>
                
                <Button
                  variant="text"
                  onClick={() => setGameMode('menu')}
                >
                  Back
                </Button>
              </Box>
            </Box>
          </Paper>
          </Box>
        </Container>
        
      </>
    );
  }

  if (gameMode === 'room') {
    return (
      <>
        <Container maxWidth="md">
          {/* Title header */}
          <div className="title-header">
            <Typography variant="h3" component="h1">
              Anime Opening Quiz
            </Typography>
          </div>

          <Box className="quiz-container">
          <Box style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
            <Typography variant="h4" component="h2" color="primary">
              Room: {roomId}
            </Typography>
            <Tooltip title="Copy Room ID">
              <IconButton onClick={handleCopyRoomId} size="small">
                <ContentCopy />
              </IconButton>
            </Tooltip>
          </Box>
          
          <Paper className="quiz-paper">
            <Box className="room-lobby">
              <Typography variant="h5" gutterBottom>
                {isHost ? 'You are the host' : 'Waiting for host to start...'}
              </Typography>
              
              {/* Game Settings Section */}
              <Box style={{ marginBottom: '24px' }}>
                <Typography variant="h6" gutterBottom>
                  Game Settings
                </Typography>
                
                {isHost ? (
                  <>
                    <TextField
                      type="number"
                      label="Number of Openings"
                      value={totalOpenings}
                      onChange={(e) => {
                        const newValue = Math.max(1, parseInt(e.target.value) || 1);
                        setTotalOpenings(newValue);
                        // Auto-update room settings
                        if (socket) {
                          socket.emit('update-room-settings', {
                            roomId,
                            totalOpenings: newValue,
                            difficulties: selectedDifficulties
                          });
                        }
                      }}
                      inputProps={{ min: 1 }}
                      size="small"
                      style={{ marginBottom: '16px', width: '200px' }}
                    />
                    
                    <Typography variant="subtitle1" gutterBottom>
                      Difficulty Settings
                    </Typography>
                    <Box className="difficulty-buttons" style={{ display: 'flex', justifyContent: 'center', gap: '12px', marginBottom: '8px' }}>
                      <Button
                        variant={selectedDifficulties.includes('easy') ? 'contained' : 'outlined'}
                        color="success"
                        size="small"
                        onClick={() => {
                          if (selectedDifficulties.includes('easy')) {
                            const newDifficulties = selectedDifficulties.filter(d => d !== 'easy');
                            setSelectedDifficulties(newDifficulties);
                            // Auto-update room settings
                            if (socket && newDifficulties.length > 0) {
                              socket.emit('update-room-settings', {
                                roomId,
                                totalOpenings,
                                difficulties: newDifficulties
                              });
                            }
                          } else {
                            const newDifficulties = [...selectedDifficulties, 'easy'];
                            setSelectedDifficulties(newDifficulties);
                            // Auto-update room settings
                            if (socket) {
                              socket.emit('update-room-settings', {
                                roomId,
                                totalOpenings,
                                difficulties: newDifficulties
                              });
                            }
                          }
                        }}
                      >
                        Easy
                      </Button>
                      <Button
                        variant={selectedDifficulties.includes('medium') ? 'contained' : 'outlined'}
                        color="warning"
                        size="small"
                        onClick={() => {
                          if (selectedDifficulties.includes('medium')) {
                            const newDifficulties = selectedDifficulties.filter(d => d !== 'medium');
                            setSelectedDifficulties(newDifficulties);
                            // Auto-update room settings
                            if (socket && newDifficulties.length > 0) {
                              socket.emit('update-room-settings', {
                                roomId,
                                totalOpenings,
                                difficulties: newDifficulties
                              });
                            }
                          } else {
                            const newDifficulties = [...selectedDifficulties, 'medium'];
                            setSelectedDifficulties(newDifficulties);
                            // Auto-update room settings
                            if (socket) {
                              socket.emit('update-room-settings', {
                                roomId,
                                totalOpenings,
                                difficulties: newDifficulties
                              });
                            }
                          }
                        }}
                      >
                        Medium
                      </Button>
                      <Button
                        variant={selectedDifficulties.includes('hard') ? 'contained' : 'outlined'}
                        color="error"
                        size="small"
                        onClick={() => {
                          if (selectedDifficulties.includes('hard')) {
                            const newDifficulties = selectedDifficulties.filter(d => d !== 'hard');
                            setSelectedDifficulties(newDifficulties);
                            // Auto-update room settings
                            if (socket && newDifficulties.length > 0) {
                              socket.emit('update-room-settings', {
                                roomId,
                                totalOpenings,
                                difficulties: newDifficulties
                              });
                            }
                          } else {
                            const newDifficulties = [...selectedDifficulties, 'hard'];
                            setSelectedDifficulties(newDifficulties);
                            // Auto-update room settings
                            if (socket) {
                              socket.emit('update-room-settings', {
                                roomId,
                                totalOpenings,
                                difficulties: newDifficulties
                              });
                            }
                          }
                        }}
                        sx={{
                          '&.MuiButton-contained': {
                            backgroundColor: '#d32f2f !important',
                            color: 'white !important',
                            '&:hover': {
                              backgroundColor: '#b71c1c !important'
                            }
                          },
                          '&.MuiButton-outlined': {
                            borderColor: '#d32f2f !important',
                            color: '#d32f2f !important',
                            '&:hover': {
                              borderColor: '#b71c1c !important',
                              backgroundColor: 'rgba(211, 47, 47, 0.04) !important'
                            }
                          }
                        }}
                      >
                        Hard
                      </Button>
                    </Box>
                    <Typography variant="body2" color="text.secondary" style={{ marginBottom: '16px', textAlign: 'center' }}>
                      Easy: Popular anime • Medium: Well-known but less mainstream • Hard: Obscure/niche anime
                    </Typography>
                  </>
                ) : (
                  <>
                    <Typography variant="body1" color="text.secondary">
                      Openings: {totalOpenings}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      Difficulties: {roomDifficulties.map(d => d.charAt(0).toUpperCase() + d.slice(1)).join(', ')}
                    </Typography>
                  </>
                )}
              </Box>
              
              <Typography variant="h6" gutterBottom>
                Players ({players.length}):
              </Typography>
              
              <List>
                {players.map((player) => (
                  <ListItem key={player.id}>
                    <ListItemText 
                      primary={`${player.name} ${player.id === socket?.id ? '(You)' : ''}`}
                      secondary={isHost && player.id === socket?.id ? 'Host' : ''}
                    />
                  </ListItem>
                ))}
              </List>
              
              <Box className="room-buttons">
                {isHost && (
                  <Button
                    variant="contained"
                    onClick={handleStartGame}
                    disabled={players.length === 0 || selectedDifficulties.length === 0}
                  >
                    Start Game
                  </Button>
                )}
                
                <Button
                  variant="text"
                  onClick={() => {
                    socket?.disconnect();
                    setGameMode('menu');
                  }}
                >
                  Leave Room
                </Button>
              </Box>
            </Box>
          </Paper>
          
          {/* Copy Snackbar */}
          <Snackbar
            open={copySnackbarOpen}
            autoHideDuration={2000}
            onClose={() => setCopySnackbarOpen(false)}
            message="Room ID copied to clipboard!"
          />
          </Box>
        </Container>
        
      </>
    );
  }

  if (gameMode === 'game') {
    const currentPlayer = players.find(p => p.id === socket?.id);
    const sortedPlayers = players.sort((a, b) => b.score - a.score);
    
    return (
      <>
        <Container maxWidth="md">
          {/* Title header */}
          <div className="title-header">
            <Typography variant="h3" component="h1">
              Anime Opening Quiz
            </Typography>
          </div>

          <Box className="quiz-container">
          {loading ? (
            <div className="loading-container">
              <CircularProgress />
            </div>
          ) : currentOpening ? (
            <Paper className="quiz-paper">
              {/* Game Progress Header */}
              <Box className="game-header">
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h5">
                    Opening {currentOpeningIndex} of {totalOpenings}
                  </Typography>
                  <Button 
                    variant="text" 
                    size="small"
                    onClick={() => {
                      if (window.confirm('Are you sure you want to quit the game?')) {
                        handleLeaveRoom();
                        onBackToMenu();
                      }
                    }}
                    sx={{ color: 'text.secondary' }}
                  >
                    Main Menu
                  </Button>
                </Box>
                <Box className="game-chips-container">
                  {sortedPlayers.map((player) => (
                    <Chip
                      key={player.id}
                      label={`${player.name}: ${player.score}`}
                      color={player.id === socket?.id ? 'primary' : 'default'}
                      size="small"
                      variant={player.id === socket?.id ? 'filled' : 'outlined'}
                    />
                  ))}
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={(currentOpeningIndex - 1) / totalOpenings * 100}
                  className="game-progress-bar"
                />
              </Box>

              {/* Status and Timer Container */}
              <Box className="status-timer-container">
                <Typography variant="h6" color="text.secondary">
                  {isTimeUp 
                    ? (hasGuessed ? (isCorrect ? 'Correct!' : 'Wrong!') : 'Too late!') 
                    : videoErrorMessage 
                    ? 'Video Error' 
                    : !playerReady 
                    ? 'Loading Video...' 
                    : isPlaying 
                    ? 'Playing...' 
                    : 'Video Ready'}
                </Typography>
                {videoErrorMessage && (
                  <Alert severity="warning" sx={{ mt: 1, mb: 1 }}>
                    {videoErrorMessage}
                  </Alert>
                )}
                <Box className="timer-progress-container">
                  <LinearProgress 
                    variant="determinate" 
                    value={(15 - timeLeft) * (100/15)} 
                    className="timer-progress-bar"
                  />
                  <Typography variant="body2" color="text.secondary" className="timer-text">
                    {timeLeft}s left
                  </Typography>
                </Box>
                {/* Host Skip Button - only show during active gameplay */}
                {isHost && !isTimeUp && (
                  <Box className="skip-button-container" style={{ marginTop: '8px' }}>
                    <Button 
                      variant="contained" 
                      size="medium"
                      onClick={handleSkipOpening}
                      color="warning"
                      className="skip-button"
                    >
                      Skip to Answer
                    </Button>
                  </Box>
                )}
                {isTimeUp && (
                  <Box className="next-button-container">
                    {hasGuessed && (
                      <Typography variant="body2" color="text.secondary" className="points-display">
                        {isCorrect 
                          ? pointBreakdown 
                            ? `${pointBreakdown.base} points for ${pointBreakdown.difficulty}${pointBreakdown.time > 0 ? ` + ${pointBreakdown.time} for speed` : ''}${pointBreakdown.first ? ` + ${pointBreakdown.first} for being first!` : ''} = ${pointBreakdown.base + pointBreakdown.time + (pointBreakdown.first || 0)} total!`
                            : `+Points earned! Well done!`
                          : '+0 points'
                        }
                      </Typography>
                    )}
                    {isHost && (
                      <Button 
                        variant="outlined" 
                        onClick={handleNextOpening}
                        color="primary"
                        className="next-button"
                      >
                        {currentOpeningIndex >= totalOpenings ? 'Finish Game' : 'Next Opening'}
                      </Button>
                    )}
                    {!isHost && (
                      <Typography variant="body2" color="text.secondary">
                        Waiting for host to continue...
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>

              <TextField
                fullWidth
                label="Guess the anime"
                value={hasGuessed ? playerGuess : searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={isTimeUp || hasGuessed}
                className="search-input"
                ref={searchInputRef}
              />

              {searchResults.length > 0 && !isTimeUp && !hasGuessed && (
                <List className="search-results-list">
                  {searchResults.map((result, index) => (
                    <ListItem key={result.id} disablePadding>
                      <ListItemButton 
                        onClick={() => handleGuess(result.title)}
                        selected={index === selectedIndex}
                        className={index === selectedIndex ? 'selected-result' : ''}
                      >
                        <ListItemText primary={result.title} />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              )}

              {isTimeUp && (
                <Box className="answer-container">
                  <Typography 
                    variant="h5" 
                    className={hasGuessed ? (isCorrect ? "success-text" : "error-text") : "warning-text"}
                    gutterBottom
                  >
                    {hasGuessed ? (
                      isCorrect ? "Correct!" : `Wrong! The answer is: ${currentOpening?.title}`
                    ) : (
                      `Too late! The answer is: ${currentOpening?.title}`
                    )}
                  </Typography>
                  
                  {/* All Player Guesses Display */}
                  {allPlayerGuesses.length > 0 && (
                    <Box className="all-guesses-container" style={{ marginTop: '16px' }}>
                      <Typography variant="h6" gutterBottom>
                        What Everyone Guessed:
                      </Typography>
                      <Box className="guess-chips">
                        {allPlayerGuesses.map((guess, index) => (
                          <Chip
                            key={index}
                            label={`${guess.playerName}: "${guess.guess}"`}
                            color={guess.isCorrect ? 'success' : 'default'}
                            variant={guess.isCorrect ? 'filled' : 'outlined'}
                            size="small"
                            style={{ margin: '2px' }}
                          />
                        ))}
                      </Box>
                    </Box>
                  )}
                </Box>
              )}

              {/* Video Player Container - Hidden until time is up for the round */}
              <Box className={`video-container ${isTimeUp ? 'visible' : 'hidden'}`}>
                <div id="youtube-player" />
              </Box>
            </Paper>
          ) : (
            <Typography>No opening available</Typography>
          )}
          </Box>
        </Container>
        
      </>
    );
  }

  if (gameMode === 'finished') {
    return (
      <>
        <Container maxWidth="md">
          {/* Title header */}
          <div className="title-header">
            <Typography variant="h3" component="h1">
              Anime Opening Quiz
            </Typography>
          </div>

          <Box className="quiz-container">
          <Paper className="quiz-paper">
            <Box className="game-results">
              <Typography variant="h4" gutterBottom color="primary">
                Game Complete! Final Rankings
              </Typography>
              
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Rank</TableCell>
                      <TableCell>Player</TableCell>
                      <TableCell align="right">Score</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {finalScores.map((player, index) => (
                      <TableRow 
                        key={player.id}
                        className={player.id === socket?.id ? 'current-player-row' : ''}
                      >
                        <TableCell>
                          {index === 0 ? '1st' : index === 1 ? '2nd' : index === 2 ? '3rd' : `${index + 1}th`}
                        </TableCell>
                        <TableCell>
                          {player.name} {player.id === socket?.id ? '(You)' : ''}
                        </TableCell>
                        <TableCell align="right">{player.score}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              
              <Box className="results-buttons">
                <Button
                  variant="contained"
                  onClick={handleReturnToRoom}
                >
                  Return to Room
                </Button>
                
                <Button
                  variant="outlined"
                  onClick={onBackToMenu}
                >
                  Main Menu
                </Button>
              </Box>
            </Box>
          </Paper>
          </Box>
        </Container>
        
      </>
    );
  }

  return null;
}

// Add TypeScript declaration for YouTube API
declare global {
  interface Window {
    YT: any;
    onYouTubeIframeAPIReady: () => void;
  }
}

export default MultiplayerApp;
